import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { 
  ArrowLeft, Database, Download, FileSpreadsheet, 
  Users, Building, FileText, Receipt, Truck, CheckCircle 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DataExport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState({});
  const [results, setResults] = useState({});

  const getToken = () => localStorage.getItem('token');

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleString('en-IN', { 
        dateStyle: 'short', 
        timeStyle: 'short' 
      });
    } catch {
      return dateStr;
    }
  };

  const exportMembers = async () => {
    setLoading(prev => ({ ...prev, members: true }));
    try {
      const response = await axios.get(`${API}/members/export/all`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      const members = response.data?.data || response.data || [];
      
      if (members.length === 0) {
        toast.warning('No members found to export');
        return;
      }

      const headers = [
        'Member ID', 'Name', 'Initials', 'Designation', 'Company Name',
        'Contact No', 'WhatsApp No', 'Email', 'Address', 'Pincode',
        'State', 'District', 'Expiry Date', 'Member Type', 'Customer Type',
        'Plan Type', 'Magazine', 'Subscription Duration', 'Amount Paid',
        'Status', 'Notes', 'Category', 'Delivery', 'Created Date', 
        'Created By', 'Last Modified', 'Modified By'
      ];

      const data = members.map(m => [
        m.member_id || '',
        m.name || '',
        m.initials || '',
        m.designation || '',
        m.company_name || '',
        m.contact_no || '',
        m.whatsapp_no || '',
        m.email || '',
        m.address || '',
        m.pincode || '',
        m.state || '',
        m.district || '',
        m.expiry_date || '',
        m.member_type || '',
        m.customer_type || '',
        m.plan_type || '',
        m.database_type || '',
        m.subscription_duration || '',
        m.amount_paid || '',
        m.status || '',
        m.notes || '',
        m.category || '',
        m.delivery || '',
        formatDate(m.created_at),
        m.created_by || '',
        formatDate(m.updated_at),
        m.updated_by || ''
      ]);

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
      
      // Set column widths
      ws['!cols'] = headers.map(() => ({ wch: 18 }));
      
      XLSX.utils.book_append_sheet(wb, ws, 'Members');
      XLSX.writeFile(wb, `Members_Backup_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      setResults(prev => ({ ...prev, members: members.length }));
      toast.success(`Exported ${members.length} members to Excel`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export members');
    } finally {
      setLoading(prev => ({ ...prev, members: false }));
    }
  };

  const exportAdvertisers = async () => {
    setLoading(prev => ({ ...prev, advertisers: true }));
    try {
      const response = await axios.get(`${API}/advertisers/export/all`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      const advertisers = response.data?.data || response.data || [];
      
      if (advertisers.length === 0) {
        toast.warning('No advertisers found to export');
        return;
      }

      const headers = [
        'Advertiser ID', 'Name', 'Initials', 'Designation', 'Company Name',
        'Contact No', 'WhatsApp No', 'Email', 'Address', 'Pincode',
        'State', 'District', 'GST No', 'PO/WO No', 'Size', 'Duration',
        'Amount', 'Payment Status', 'Status', 'Start Date', 'End Date',
        'Delivery Mode', 'Print Schedule', 'Schedule Type', 'Magazine',
        'Notes', 'Created Date', 'Created By', 'Last Modified', 'Modified By'
      ];

      const data = advertisers.map(a => [
        a.advertiser_id || '',
        a.name || '',
        a.initials || '',
        a.designation || '',
        a.company_name || '',
        a.contact_no || '',
        a.whatsapp_no || '',
        a.email || '',
        a.address || '',
        a.pincode || '',
        a.state || '',
        a.district || '',
        a.gst_no || '',
        a.po_wo_no || '',
        a.size || '',
        a.duration || '',
        a.amount || '',
        a.payment_status || '',
        a.status || '',
        a.start_date || '',
        a.end_date || `${a.end_month || ''} ${a.end_year || ''}`,
        a.delivery_mode || '',
        a.print_schedule_type || '',
        a.schedule_type || '',
        a.magazine || '',
        a.notes || '',
        formatDate(a.created_at),
        a.created_by || '',
        formatDate(a.updated_at),
        a.updated_by || ''
      ]);

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
      ws['!cols'] = headers.map(() => ({ wch: 18 }));
      
      XLSX.utils.book_append_sheet(wb, ws, 'Advertisers');
      XLSX.writeFile(wb, `Advertisers_Backup_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      setResults(prev => ({ ...prev, advertisers: advertisers.length }));
      toast.success(`Exported ${advertisers.length} advertisers to Excel`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export advertisers');
    } finally {
      setLoading(prev => ({ ...prev, advertisers: false }));
    }
  };

  const exportBills = async () => {
    setLoading(prev => ({ ...prev, bills: true }));
    try {
      const response = await axios.get(`${API}/bills/export/all`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      const allBills = response.data?.data || response.data || [];
      
      if (allBills.length === 0) {
        toast.warning('No bills found to export');
        return;
      }

      const headers = [
        'Bill No', 'Financial Year', 'Magazine', 'Document Type', 'Entity ID',
        'Billing Name', 'Billing Address', 'Billing State', 'Billing Pincode',
        'GST No', 'Ad Size', 'Duration', 'Issues', 'Gross Amount', 'Discount',
        'Net Amount', 'GST Type', 'CGST', 'SGST', 'IGST', 'Total Amount',
        'Amount in Words', 'Status', 'Created Date', 'Created By'
      ];

      const data = allBills.map(b => [
        b.bill_no || '',
        b.financial_year || '',
        b.magazine || '',
        b.document_type || 'Tax Invoice',
        b.entity_id || '',
        b.billing_name || '',
        b.billing_address || '',
        b.billing_state || '',
        b.billing_pincode || '',
        b.gst_no || '',
        b.ad_size || '',
        b.duration || '',
        b.issues || '',
        b.gross_amount || '',
        b.discount || '',
        b.net_amount || '',
        b.gst_type || '',
        b.cgst || '',
        b.sgst || '',
        b.igst || '',
        b.total_amount || '',
        b.amount_in_words || '',
        b.status || '',
        formatDate(b.created_at),
        b.created_by || ''
      ]);

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
      ws['!cols'] = headers.map(() => ({ wch: 18 }));
      
      XLSX.utils.book_append_sheet(wb, ws, 'Bills');
      XLSX.writeFile(wb, `Bills_Backup_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      setResults(prev => ({ ...prev, bills: allBills.length }));
      toast.success(`Exported ${allBills.length} bills to Excel`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export bills');
    } finally {
      setLoading(prev => ({ ...prev, bills: false }));
    }
  };

  const exportProformaInvoices = async () => {
    setLoading(prev => ({ ...prev, proforma: true }));
    try {
      const response = await axios.get(`${API}/proforma-invoices/export/all`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      const invoices = response.data?.data || response.data || [];
      
      if (invoices.length === 0) {
        toast.warning('No proforma invoices found to export');
        setResults(prev => ({ ...prev, proforma: 0 }));
        return;
      }

      const headers = [
        'PI No', 'Magazine', 'Type', 'Entity ID', 'Billing Name',
        'Billing Address', 'Billing State', 'Billing Pincode', 'GST No',
        'Ad Size', 'Duration', 'Issues', 'Gross Amount', 'Discount',
        'Net Amount', 'GST Type', 'CGST', 'SGST', 'IGST', 'Total Amount',
        'Amount in Words', 'Status', 'Created Date', 'Created By'
      ];

      const data = invoices.map(pi => [
        pi.proforma_invoice_no || '',
        pi.magazine || '',
        pi.type || '',
        pi.entity_id || '',
        pi.billing_name || '',
        pi.billing_address || '',
        pi.billing_state || '',
        pi.billing_pincode || '',
        pi.gst_no || '',
        pi.ad_size || '',
        pi.duration || '',
        pi.issues || '',
        pi.gross_amount || '',
        pi.discount || '',
        pi.net_amount || '',
        pi.gst_type || '',
        pi.cgst || '',
        pi.sgst || '',
        pi.igst || '',
        pi.total_amount || '',
        pi.amount_in_words || '',
        pi.status || '',
        formatDate(pi.created_at),
        pi.created_by || ''
      ]);

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
      ws['!cols'] = headers.map(() => ({ wch: 18 }));
      
      XLSX.utils.book_append_sheet(wb, ws, 'Proforma Invoices');
      XLSX.writeFile(wb, `Proforma_Invoices_Backup_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      setResults(prev => ({ ...prev, proforma: invoices.length }));
      toast.success(`Exported ${invoices.length} proforma invoices to Excel`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export proforma invoices');
    } finally {
      setLoading(prev => ({ ...prev, proforma: false }));
    }
  };

  const exportDispatches = async () => {
    setLoading(prev => ({ ...prev, dispatches: true }));
    try {
      const response = await axios.get(`${API}/dispatches/export/all`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      const dispatches = response.data?.data || response.data || [];
      
      if (dispatches.length === 0) {
        toast.warning('No dispatches found to export');
        setResults(prev => ({ ...prev, dispatches: 0 }));
        return;
      }

      const headers = [
        'Dispatch ID', 'Entity ID', 'Entity Type', 'Magazine', 'Month', 'Year',
        'Mode', 'Courier Name', 'Tracking No', 'Weight', 'Charges',
        'Status', 'Dispatch Date', 'Failed Reason', 'Re-dispatch Info',
        'Created Date', 'Created By'
      ];

      const data = dispatches.map(d => [
        d.dispatch_id || d.id || '',
        d.entity_id || d.member_id || d.advertiser_id || '',
        d.entity_type || '',
        d.magazine || d.database_type || '',
        d.month || '',
        d.year || '',
        d.mode || d.dispatch_mode || '',
        d.courier_name || '',
        d.tracking_no || '',
        d.weight || '',
        d.charges || '',
        d.status || '',
        d.dispatch_date || '',
        d.failed_reason || '',
        d.redispatch_info || '',
        formatDate(d.created_at),
        d.created_by || ''
      ]);

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
      ws['!cols'] = headers.map(() => ({ wch: 18 }));
      
      XLSX.utils.book_append_sheet(wb, ws, 'Dispatches');
      XLSX.writeFile(wb, `Dispatches_Backup_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      setResults(prev => ({ ...prev, dispatches: dispatches.length }));
      toast.success(`Exported ${dispatches.length} dispatches to Excel`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export dispatches');
    } finally {
      setLoading(prev => ({ ...prev, dispatches: false }));
    }
  };

  const exportAll = async () => {
    toast.info('Starting complete backup export...');
    await exportMembers();
    await exportAdvertisers();
    await exportBills();
    await exportProformaInvoices();
    await exportDispatches();
    toast.success('Complete backup export finished!');
  };

  const ExportCard = ({ title, icon: Icon, color, onClick, loadingKey, resultKey, description }) => (
    <div className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${color}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Icon size={28} className={color.replace('border-', 'text-')} />
          <h3 className="text-lg font-bold text-gray-800">{title}</h3>
        </div>
        {results[resultKey] !== undefined && (
          <span className="flex items-center text-green-600 text-sm">
            <CheckCircle size={16} className="mr-1" />
            {results[resultKey]} exported
          </span>
        )}
      </div>
      <p className="text-gray-600 text-sm mb-4">{description}</p>
      <Button 
        onClick={onClick}
        disabled={loading[loadingKey]}
        className={`w-full ${color.replace('border-', 'bg-').replace('-500', '-600')} hover:${color.replace('border-', 'bg-').replace('-500', '-700')} text-white`}
        data-testid={`export-${loadingKey}-btn`}
      >
        {loading[loadingKey] ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Exporting...
          </>
        ) : (
          <>
            <Download size={18} className="mr-2" />
            Export to Excel
          </>
        )}
      </Button>
    </div>
  );

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-indigo-50 py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
                <ArrowLeft className="mr-2" size={18} />
                Back
              </Button>
              <h1 className="text-3xl font-bold text-indigo-700" data-testid="page-title">
                <Database className="inline mr-3" size={32} />
                Data Export / Backup
              </h1>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
            <div className="flex items-start">
              <FileSpreadsheet className="text-blue-600 mr-3 mt-1 flex-shrink-0" size={20} />
              <div className="text-sm text-blue-700">
                <p className="font-semibold mb-1">Data Backup Tool</p>
                <p>Export complete data from the database to Excel files. Each export creates a separate file with all fields. Files are named with today's date for easy organization.</p>
              </div>
            </div>
          </div>

          {/* Export All Button */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg p-6 mb-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold mb-1">Complete Backup</h2>
                <p className="text-indigo-100 text-sm">Export all data (Members, Advertisers, Bills, Proforma Invoices, Dispatches) at once</p>
              </div>
              <Button 
                onClick={exportAll}
                disabled={Object.values(loading).some(v => v)}
                className="bg-white text-indigo-700 hover:bg-indigo-100 px-6 py-3"
                data-testid="export-all-btn"
              >
                <Download size={20} className="mr-2" />
                Export All Data
              </Button>
            </div>
          </div>

          {/* Individual Export Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <ExportCard
              title="Members"
              icon={Users}
              color="border-blue-500"
              onClick={exportMembers}
              loadingKey="members"
              resultKey="members"
              description="Export all member details including contact info, address, subscription, and more."
            />

            <ExportCard
              title="Advertisers"
              icon={Building}
              color="border-green-500"
              onClick={exportAdvertisers}
              loadingKey="advertisers"
              resultKey="advertisers"
              description="Export all advertiser details including company info, ad details, payment status."
            />

            <ExportCard
              title="Bills"
              icon={Receipt}
              color="border-purple-500"
              onClick={exportBills}
              loadingKey="bills"
              resultKey="bills"
              description="Export all tax invoices with billing details, GST, amounts, and status."
            />

            <ExportCard
              title="Proforma Invoices"
              icon={FileText}
              color="border-orange-500"
              onClick={exportProformaInvoices}
              loadingKey="proforma"
              resultKey="proforma"
              description="Export all proforma invoices with complete billing information."
            />

            <ExportCard
              title="Dispatches"
              icon={Truck}
              color="border-teal-500"
              onClick={exportDispatches}
              loadingKey="dispatches"
              resultKey="dispatches"
              description="Export all dispatch records including courier details and tracking info."
            />
          </div>

          {/* Export Summary */}
          {Object.keys(results).length > 0 && (
            <div className="mt-8 bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-green-700 mb-4 flex items-center">
                <CheckCircle className="mr-2" size={24} />
                Export Summary
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {results.members !== undefined && (
                  <div className="bg-white p-3 rounded text-center">
                    <p className="text-2xl font-bold text-blue-600">{results.members}</p>
                    <p className="text-xs text-gray-600">Members</p>
                  </div>
                )}
                {results.advertisers !== undefined && (
                  <div className="bg-white p-3 rounded text-center">
                    <p className="text-2xl font-bold text-green-600">{results.advertisers}</p>
                    <p className="text-xs text-gray-600">Advertisers</p>
                  </div>
                )}
                {results.bills !== undefined && (
                  <div className="bg-white p-3 rounded text-center">
                    <p className="text-2xl font-bold text-purple-600">{results.bills}</p>
                    <p className="text-xs text-gray-600">Bills</p>
                  </div>
                )}
                {results.proforma !== undefined && (
                  <div className="bg-white p-3 rounded text-center">
                    <p className="text-2xl font-bold text-orange-600">{results.proforma}</p>
                    <p className="text-xs text-gray-600">Proforma</p>
                  </div>
                )}
                {results.dispatches !== undefined && (
                  <div className="bg-white p-3 rounded text-center">
                    <p className="text-2xl font-bold text-teal-600">{results.dispatches}</p>
                    <p className="text-xs text-gray-600">Dispatches</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DataExport;
